import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Chip, InputBase, LinearProgress } from "@mui/material";

import { FeedEntry, Medium } from "protogen/advisors_service_pb";
import { useSendTextMessage } from "services/textMessage";
import {
  SendTextMessageRequest,
  SendTextMessageResponse,
} from "protogen/text_message_service_pb";
import { useUploader } from "../creation/FileUploader";
import { SendHorizontal as SendIcon } from "lucide-react";

import {
  isMobileiOS,
  isStandaloneWebapp,
  isTouchDevice,
} from "../../common/utils";
import MessageMenu, { UploadSpeedDialAction } from "./MessageMenu";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import { UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import DragNDrop from "../common/DragNDrop";
import SimpleEditor, { Handle } from "../editor/SimpleEditor";
import { RichContent, processImages } from "../editor/utils";
import useCached from "../hooks/useCached";
import { CalendarClock, SquareUserRound, Paperclip, X } from "lucide-react";
import ScheduledMessageDialog from "./ScheduledMessageDialog";
import UploadThumbnail from "./UploadThumbnail";
import useIsMobile from "components/hooks/useIsMobile";

interface SMSInputProps {
  onSent: (e: FeedEntry) => void;
  familyRef: string | null;
  recipientRefs: string[];
  placeholder: string;
  contactEnabled?: boolean;
  forceDisabled?: boolean;
  initialContent?: string;
  onUpdateContent?: () => void;
}

const SendButton = ({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) => {
  // if (disabled) return null;
  return (
    <Button
      disabled={disabled}
      onClick={onClick}
      variant="text"
      sx={{
        minWidth: "24px",
        borderRadius: "8px",
        border: "none",
        padding: "0px",
        height: "44px",
        width: "24px",
        overflow: "hidden",
        opacity: 1,
        transition: "all 0.3s ease",
        marginLeft: 0,
        flexShrink: 0,
        "&.Mui-disabled": {
          minWidth: "0px",
          opacity: 0,
          width: 0,
          padding: 0,
          marginLeft: "-12px", // negative to offset parent's gap.
        },
        ".MuiButton-startIcon": {
          margin: 0,
        },
      }}
      startIcon={
        <span
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "44px",
          }}
        >
          <SendIcon width="24px" />
        </span>
      }
    />
  );
};

export const formatDate = (date: Date): string => {
  const now = new Date();
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
  }; // Time formatting options

  // Check if the date is today
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isToday) {
    // If it's today, return the time only
    return date.toLocaleTimeString("en-US", options);
  } else {
    // Otherwise, return "Month Day, Time"
    const dateOptions: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };
    return `${date.toLocaleDateString(
      "en-US",
      dateOptions,
    )}, ${date.toLocaleTimeString("en-US", options)}`;
  }
};

export type SendOptions = {
  scheduledDate?: Date;
  attachments: PlainMessage<UploadAttachment>[];
};

interface CompositionSectionProps {
  placeholder: string;
  contactEnabled?: boolean;
  loading: boolean;
  thumbsUp: boolean;
  onSend: (inputValue: string, options: SendOptions) => Promise<boolean>;
  handleSendCannedMessage?: (
    message: string | null,
    includeContactCard?: boolean,
  ) => void;
  unpad?: boolean;
  autoFocus?: boolean;
  maxUploadBytes?: number;
  maxUploadBytesNonImage?: number;
  forceDisabled?: boolean;
  schedulingEnabled?: boolean;
  cacheKey?: string | null;
  maxContentLength?: number;
  initialContent?: string;
  onUpdateContent?: () => void;
}
export const CompositionSection = ({
  onSend,
  loading,
  placeholder,
  handleSendCannedMessage,
  thumbsUp = false,
  contactEnabled = true,
  schedulingEnabled = false,
  unpad = false,
  autoFocus = false,
  maxUploadBytes = 4900000,
  maxUploadBytesNonImage = undefined,
  forceDisabled = false,
  cacheKey = null,
  maxContentLength, // Add maxLength prop
  initialContent = "",
  onUpdateContent = () => {},
}: CompositionSectionProps) => {
  const isTouch = isTouchDevice();
  const editorRef = useRef<Handle>(null);
  const [triggerReset, setTriggerReset] = useState(false);
  const [content, setContent] = useCached<RichContent | null>(cacheKey, null);
  const [hover, setHover] = useState(false);
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [scheduled, setScheduled] = useState<Date | undefined>(undefined);

  const resetInput = () => {
    setTriggerReset(true);
    setContent(null);
    setScheduled(undefined);
    setSchedulerOpen(false);
  };

  const {
    onUpload,
    fileUploads,
    getCompleteAttachments,
    uploadPercentage,
    clearUploads,
    removeUpload,
    uploadsInProgress,
  } = useUploader({
    maxUploadBytes: maxUploadBytes,
    maxUploadBytesNonImage: maxUploadBytesNonImage,
  });

  const handleSend = async () => {
    const attachments = [
      ...(content?.attachments || []),
      ...getCompleteAttachments(),
    ];
    if (loading || uploadsInProgress) return;
    if (!(content?.text || attachments.length) && !initialContent) return;
    const success = await onSend(content?.text || initialContent || "", {
      attachments,
      scheduledDate: scheduled,
    });
    if (success) {
      clearUploads();
      resetInput();
    }
  };

  const onCannedMessage = (
    message: string | null,
    includeContactCard?: boolean,
  ) => {
    // Lets not confused things by posting non-current content text if it's present, but if it's a contact
    // that seems fine.
    if (includeContactCard || (!content?.text && !fileUploads.length)) {
      handleSendCannedMessage &&
        handleSendCannedMessage(message, includeContactCard);
    }
  };

  useEffect(() => {
    if (!loading && autoFocus) {
      editorRef.current?.focus();
    }
  }, [loading]);

  let _initialContent = initialContent;
  if (
    content &&
    content.json &&
    // Handle case where content is empty
    content.json !== '{"type":"doc","content":[{"type":"paragraph"}]}'
  ) {
    try {
      _initialContent = JSON.parse(content.json);
    } catch (e) {
      console.error(`Error parsing initialContent: ${e}`);
    }
  }
  const disabled =
    loading ||
    (!content?.text && !fileUploads.length && !initialContent) ||
    uploadsInProgress;
  const hasExceededMaxLength = !!(
    maxContentLength &&
    content?.text.length &&
    content?.text.length > maxContentLength
  );
  const isMobile = useIsMobile();

  return (
    <DragNDrop
      enabled={!(uploadsInProgress || loading || forceDisabled)}
      setHover={setHover}
      onUpload={(files) => onUpload(files)}
    >
      <Box
        display="flex"
        sx={{
          width: "100%",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: "12px",
          ...(unpad
            ? {}
            : {
                // Need extra padding on iosWebapps since there's a little bar at the bottom.
                padding:
                  isStandaloneWebapp() && isMobileiOS()
                    ? "16px 16px 30px 0px"
                    : "16px 16px 16px 0px",
              }),
          ...(hover ? { backgroundColor: "#e8f4f8" } : {}),
        }}
      >
        <div style={{ position: "relative", width: "40px", height: "100%" }}>
          <MessageMenu disabled={!contactEnabled || forceDisabled}>
            {thumbsUp && (
              <SpeedDialAction
                icon={"👍"}
                tooltipPlacement="right"
                tooltipTitle={"Send 👍"}
                tooltipOpen={isTouch}
                onClick={() => onCannedMessage("👍", false)}
              />
            )}
            <UploadSpeedDialAction
              disabled={uploadsInProgress}
              icon={<Paperclip />}
              tooltipOpen={isTouch}
              onChange={(e) => {
                if (e.target.files) {
                  const filesArray = Array.from(e.target.files);
                  processImages(
                    filesArray,
                    null, // editor not needed for external handler
                    onUpload, // this won't be used since we have externalAttachmentHandler
                    () => {}, // setAttachments not needed for external handler
                    false, // inlineAttachmentsEnabled
                    onUpload, // externalAttachmentHandler - this will be called with processed files
                  );
                }
              }}
              tooltipPlacement="right"
              tooltipTitle={"Add an attachment"}
            />
            <SpeedDialAction
              icon={<SquareUserRound />}
              tooltipPlacement="right"
              tooltipTitle={"Send contact card"}
              tooltipOpen={isTouch}
              onClick={() => onCannedMessage(null, true)}
            />
            {schedulingEnabled && (
              <SpeedDialAction
                icon={<CalendarClock />}
                tooltipPlacement="right"
                tooltipTitle={"Schedule message"}
                tooltipOpen={isTouch}
                onClick={() => setSchedulerOpen(true)}
              />
            )}
          </MessageMenu>
          {uploadPercentage > 0 && (
            <LinearProgress
              variant="determinate"
              value={uploadPercentage}
              sx={{
                position: "absolute",
                bottom: "0px",
                width: "28px",
                left: "14px",
                boxShadow: "none",
              }}
            />
          )}
        </div>
        <div
          style={{
            position: "relative",
            flexGrow: 1,
            width: "10px",
          }}
        >
          {scheduled && (
            <Box
              display="flex"
              sx={{
                flexDirection: "row",
                gap: "6px",
                flexWrap: "wrap",
                marginBottom: "10px",
              }}
            >
              <Chip
                sx={{
                  backgroundColor: "#F2F2F2",
                  borderColor: "#E2E3E4",
                }}
                label={`Scheduled for ${formatDate(scheduled)}`}
                variant="outlined"
                deleteIcon={<X />}
                onClick={() => setSchedulerOpen(true)}
                onDelete={() => setScheduled(undefined)}
              />
            </Box>
          )}
          <SimpleEditor
            ref={editorRef}
            disabled={loading || !contactEnabled || forceDisabled}
            placeholder={placeholder}
            initialContent={_initialContent}
            setContent={(content) => {
              setContent(content);
              onUpdateContent();
            }}
            attachmentsEnabled={true}
            onKeyDown={(_, e: KeyboardEvent) => {
              // Windows may send \r\n for Enter key
              if (
                (e.key === "Enter" || e.key === "\r") &&
                !e.shiftKey &&
                !isTouch
              ) {
                handleSend();
                return true;
              }
            }}
            inlineAttachmentsEnabled={false}
            attachmentActionEnabled={false}
            primaryAction={
              <SendButton
                onClick={handleSend}
                disabled={disabled || !contactEnabled || forceDisabled}
              />
            }
            triggerReset={triggerReset}
            onReset={() => {
              setTriggerReset(false);
            }}
            urlShortenerEnabled={true}
            isRichTextEnabled={false}
            externalAttachmentHandler={onUpload}
            inputSx={
              hasExceededMaxLength
                ? {
                    borderColor: "red",
                  }
                : {}
            }
          />
          {/* FileUploads */}
          {fileUploads.length > 0 && (
            <Box
              display="flex"
              sx={{
                flexDirection: "row",
                gap: "6px",
                flexWrap: "wrap",
                marginTop: isMobile ? "16px" : "12px",
              }}
            >
              {fileUploads.map((fileUpload, i) => (
                <UploadThumbnail
                  file={fileUpload}
                  removeUpload={removeUpload}
                  key={i}
                />
              ))}
            </Box>
          )}

          {/* Error Message for Max Length Exceeded */}
          {hasExceededMaxLength && (
            <Box
              display="flex"
              sx={{
                color: "red",
                fontSize: "12px",
                margin: "4px",
              }}
            >
              Maximum length of {maxContentLength} characters exceeded.
            </Box>
          )}
        </div>
      </Box>

      <ScheduledMessageDialog
        open={schedulerOpen}
        value={scheduled}
        onUpdate={async (d) => {
          setScheduled(d);
        }}
        onClose={() => {
          setSchedulerOpen(false);
        }}
      />
    </DragNDrop>
  );
};

export const ShellCompositionSection = ({
  onFocus,
  placeholder,
  disabled = false,
  unpad = false,
}: {
  onFocus: () => void;
  placeholder: string;
  disabled?: boolean;
  unpad?: boolean;
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  return (
    <Box
      display="flex"
      sx={{
        justifyContent: "center",
        alignItems: "start",
        gap: "12px",
        ...(unpad ? {} : { padding: "16px 16px 16px 0px" }),
      }}
    >
      <div style={{ position: "relative", width: "34px" }}>
        <MessageMenu disabled={true}>{""}</MessageMenu>
      </div>
      <div style={{ position: "relative", flexGrow: 1 }}>
        <div
          style={{
            padding: "9px 30px 9px 14px",
            borderRadius: "8px",
            border: "1px solid #D0D5DD",
            background: "var(--base-white, #FFF)",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
          }}
        >
          <InputBase
            inputRef={inputRef}
            autoComplete="off"
            fullWidth
            placeholder={placeholder}
            disabled={disabled}
            multiline
            maxRows={6}
            sx={{
              padding: 0,
            }}
            onFocus={() => {
              onFocus();
            }}
          />
        </div>
      </div>
      <SendButton onClick={() => {}} disabled={true} />
    </Box>
  );
};

export default ({
  familyRef,
  onSent,
  recipientRefs,
  placeholder,
  contactEnabled = true,
  forceDisabled = false,
  initialContent = "",
  onUpdateContent = () => {},
}: SMSInputProps) => {
  const callback = (resp: SendTextMessageResponse) => {
    const message = resp.textMessage;
    onSent(
      new FeedEntry({
        ref: message.ref,
        senderRef: message.senderRef,
        timestampSec: message.timestampSec,
        medium: Medium.SMS,
        textMessage: message,
        recipientRefs: recipientRefs,
      }),
    );
  };
  const { loading, request } = useSendTextMessage();
  const handleSend = async (inputValue: string, options: SendOptions) => {
    if (loading) return false;
    const resp = await request(
      new SendTextMessageRequest({
        familyRef: familyRef || "",
        memberRefs: recipientRefs,
        content: inputValue,
        includeContactCard: false,
        attachments: options.attachments,
        scheduledSendSecUTC: BigInt(
          Math.ceil((options.scheduledDate?.getTime() || 0) / 1000),
        ),
      }),
    );
    if (resp) {
      callback(resp);
      return true;
    }
    return false;
  };
  const handleSendCannedMessage = async (
    message: string | null,
    includeContactCard?: boolean,
  ) => {
    const resp = await request(
      new SendTextMessageRequest({
        familyRef: familyRef || "",
        memberRefs: recipientRefs,
        content: message || "",
        includeContactCard: includeContactCard || false,
        attachments: [],
        scheduledSendSecUTC: BigInt(0),
      }),
    );
    if (resp) {
      callback(resp);
      return true;
    }
    return false;
  };

  return (
    <CompositionSection
      contactEnabled={contactEnabled}
      onSend={handleSend}
      placeholder={placeholder}
      handleSendCannedMessage={handleSendCannedMessage}
      loading={loading}
      thumbsUp={true}
      forceDisabled={forceDisabled}
      schedulingEnabled={true}
      cacheKey={
        familyRef
          ? `sms-compose-family:${familyRef}`
          : `sms-compose-contacts:${recipientRefs.join("|")}`
      }
      // Twilio has a lower limit for non-images
      // https://help.twilio.com/articles/360018832773-Twilio-Programmable-SMS-Supported-File-Types-and-Size-Limits-for-MMS-Media-Messages#h_01HBGHJ4R9BKZCF9QVYTCSARSM
      maxUploadBytesNonImage={1000000}
      maxContentLength={1599} // Twilio max is 1600
      initialContent={initialContent}
      onUpdateContent={onUpdateContent}
    />
  );
};
